import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Redirect } from "react-router";

import {
  Add as AddIcon,
  Person as BuyerIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  ShoppingCart as EmptyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  IconButton,
  Chip,
  MenuItem,
} from "@mui/material";

import { withTranslation, WithTranslation } from "react-i18next";
import DiversionHeader from "./DiversionHeader";

import axios from "../../helpers/axios";
import { isUatEnvironment } from "../../helpers/uatMasking";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export interface IProps {
  user: {
    id: number;
    name: string;
    selectedHierarchies: any[];
    selectedHierarchyIds: any[];
    email?: string;
    role?: string;
    is_active?: boolean;
  };
}

class Buyers extends React.Component<
  RouteComponentProps & IProps & WithTranslation
> {
  state = {
    showModal: false,
    selectedCity: [] as any[],
    editBuyerId: null as number | null,
    deleteId: null as number | null,
    formData: {
      buyerName: "",
      contactPerson: "",
      phone: "",
      gstNo: "",
      type: "",
      documents: [] as string[],
    },
    documentPreviews: [] as string[],
    buyers: [] as any[],
    existingDocuments: [] as any[],
    removedDocumentIds: [] as number[],
  };

  componentDidMount() {
    this.fetchBuyers();
  }
  private renderMaskedValue = (value: React.ReactNode) => (
    <Box
      component="span"
      sx={
        isUatEnvironment
          ? {
              filter: "blur(4px)",
              opacity: 0.5,
              userSelect: "none",
            }
          : undefined
      }
    >
      {value}
    </Box>
  );

  fetchBuyers = async () => {
    try {
      const hierarchyIds = this.props.user.selectedHierarchies.map(
        (h: any) => h.id
      );

      const response = await axios.get("listBuyers", {
        params: { hierarchy_ids: hierarchyIds },
      });

      if (response.data.success === true) {
        this.setState({ buyers: response.data.data || [] });
      } else {
        toast.error(response.data.msg || "Failed to fetch buyers");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.msg || "Server error");
    }
  };
  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      formData: {
        ...this.state.formData,
        [e.target.name]: e.target.value,
      },
    });
  };
  handleSaveBuyer = async () => {
    const { selectedCity, formData, editBuyerId } = this.state;

    if (
      selectedCity.length === 0 ||
      !formData.buyerName ||
      !formData.contactPerson ||
      !formData.phone ||
      !formData.type
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    const payload: any = {
      hierarchy_ids: selectedCity.map((city: any) => city.id),
      buyer_company_name: formData.buyerName,
      contact_person_name: formData.contactPerson,
      buyer_phone: formData.phone,
      gst_number: formData.gstNo,
      type: formData.type,
      documents: formData.documents,
      removed_document_ids: this.state.removedDocumentIds,
    };

    if (editBuyerId) payload.buyer_id = editBuyerId;

    try {
      const response = await axios.post(
        editBuyerId ? "editBuyer" : "addBuyer",
        payload
      );

      if (response.data.success === true) {
        toast.success(response.data.msg || "Success");
        this.setState({
          showModal: false,
          editBuyerId: null,
          selectedCity: [],
          documentPreviews: [],
          existingDocuments: [],
          removedDocumentIds: [],
          formData: {
            buyerName: "",
            contactPerson: "",
            phone: "",
            gstNo: "",
            documents: [],
          },
        });
        this.fetchBuyers();
      } else {
        toast.error(response.data.msg);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.msg || "Server error");
    }
  };
  handleEdit = (buyer: any) => {
    const selectedCities = this.props.user.selectedHierarchies.filter(
      (h: any) => buyer.hierarchy_ids.includes(h.id)
    );
    const existingPreviews = buyer.documents
      ? buyer.documents.map((doc: any) => doc.url)
      : [];

    this.setState({
      showModal: true,
      editBuyerId: buyer.id,
      selectedCity: selectedCities,
      formData: {
        buyerName: buyer.buyer_company_name,
        contactPerson: buyer.contact_person_name,
        phone: buyer.buyer_phone,
        gstNo: buyer.gst_number,
        type: buyer.type,
        documents: [],
      },
      existingDocuments: buyer.documents || [],
      documentPreviews: existingPreviews,
      removedDocumentIds: [],
    });
  };

  handleRemoveExistingDocument = (docId: number) => {
    this.setState((prev: any) => {
      const docToRemove = prev.existingDocuments.find(
        (d: any) => d.id === docId
      );
      const newExistingDocuments = prev.existingDocuments.filter(
        (d: any) => d.id !== docId
      );

      const newDocumentPreviews = prev.documentPreviews.filter(
        (preview: string) => preview !== docToRemove?.url
      );

      return {
        existingDocuments: newExistingDocuments,
        documentPreviews: newDocumentPreviews,
        removedDocumentIds: [...prev.removedDocumentIds, docId],
      };
    });
  };

  handleDeleteBuyer = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    try {
      const response = await axios.post("deleteBuyer", {
        buyer_id: deleteId,
      });

      if (response.data.success === true) {
        toast.success("Buyer deleted successfully");
        this.setState({ deleteId: null });
        this.fetchBuyers();
      } else {
        toast.error(response.data.msg);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.msg || "Server error");
    }
  };

  handleMultipleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const { formData, documentPreviews } = this.state;
    const newDocuments = [...formData.documents];
    const newPreviews = [...documentPreviews];

    // Convert each file to base64
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        newDocuments.push(reader.result as string);
        newPreviews.push(reader.result as string);

        this.setState({
          formData: {
            ...formData,
            documents: newDocuments,
          },
          documentPreviews: newPreviews,
        });
      };
      reader.readAsDataURL(file);
    });
  };

  handleRemoveDocument = (index: number) => {
    const { formData, documentPreviews, existingDocuments } = this.state;

    const newDocStartIndex = existingDocuments.length;
    const actualIndex = index - newDocStartIndex;

    if (actualIndex >= 0) {
      const newDocuments = formData.documents.filter(
        (_, i) => i !== actualIndex
      );
      const newPreviews = documentPreviews.filter((_, i) => i !== index);

      this.setState({
        formData: {
          ...formData,
          documents: newDocuments,
        },
        documentPreviews: newPreviews,
      });
    }
  };

render() {
    if (!localStorage.getItem("token")) {
      return <Redirect to="/login" />;
    }

    const { user } = this.props;
    
    // Handle case where user might be undefined or missing required properties
    if (!user || !user.selectedHierarchies) {
      return <Redirect to="/login" />;
    }
    const {
      showModal,
      selectedCity,
      formData,
      buyers,
      deleteId,
      documentPreviews,
      existingDocuments,
    } = this.state;
    const primaryGreen = "#204d36";

return (
      <Box sx={{ p: 2 }}>
        <ToastContainer />
        <DiversionHeader 
          user={{
          ...user,
          email: user.email ?? "",
          role: user.role ?? "",
          is_active: user.is_active ?? false,
        }}     
        />

        {/* MAIN CARD */}
        <Box
          sx={{
            mt: 4,
            background: "#fff",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            maxWidth: "1200px",
            mx: "auto",
          }}
        >
          <Box
            sx={{
              background: "#f8f9fa",
              borderRadius: "12px",
              p: 2,
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: "1px solid #e0e0e0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  background: primaryGreen,
                  p: 1.25,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BuyerIcon sx={{ color: "#fff", fontSize: 28 }} />
              </Box>

              <h1
                style={{
                  margin: 0,
                  fontSize: 28,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                Buyers
              </h1>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              sx={{
                background: primaryGreen,
                fontWeight: 600,
                textTransform: "none",
                px: 2,
                py: 0.75,
                fontSize: "14px",
              }}
              onClick={() =>
                this.setState({
                  showModal: true,
                  editBuyerId: null,
                  selectedCity: [],
                  documentPreviews: [],
                  existingDocuments: [],
                  removedDocumentIds: [],
                  formData: {
                    buyerName: "",
                    contactPerson: "",
                    phone: "",
                    gstNo: "",
                    documents: [],
                  },
                })
              }
            >
              Add Buyer
            </Button>
          </Box>
          <Box
            sx={{
              background: "white",
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #e0e0e0",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8f9fa" }}>
                  {[
                    "Buyer Name",
                    "Contact Person",
                    "Phone",
                    "GST No",
                    "Type",
                    "Documents",
                    "Actions",
                  ].map((header) => (
                    <th
                      key={header}
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        border: "1px solid #e0e0e0",
                        fontWeight: "700",
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {buyers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ padding: "48px", textAlign: "center" }}
                    >
                      <BuyerIcon style={{ fontSize: 48, color: "#ccc" }} />
                      <p>No buyers added yet.</p>
                    </td>
                  </tr>
                ) : (
                  buyers.map((buyer: any, index: number) => (
                    <tr key={index}>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        {this.renderMaskedValue(buyer.buyer_company_name)}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        {this.renderMaskedValue(buyer.contact_person_name)}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        {this.renderMaskedValue(buyer.buyer_phone)}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        {this.renderMaskedValue(buyer.gst_number || "-")}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        {buyer.type}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        {buyer.documents && buyer.documents.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                              alignItems: "center",
                            }}
                          >
                            {buyer.documents.map((doc: any, i: number) => (
                              <Box
                                key={i}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(doc.url, "_blank")}
                                >
                                  <ViewIcon />
                                </IconButton>
                                <span style={{ fontSize: "13px" }}>
                                  {doc.name || `Document ${i + 1}`}
                                </span>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <EmptyIcon sx={{ color: "#ccc" }} />
                        )}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => this.handleEdit(buyer)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => this.setState({ deleteId: buyer.id })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        </Box>
        <Dialog
          open={showModal}
          onClose={() => this.setState({ showModal: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {this.state.editBuyerId ? "Edit Buyer" : "Add Buyer"}
          </DialogTitle>
          <DialogContent>
            <Autocomplete
              multiple
              options={user.selectedHierarchies}
              getOptionLabel={(o: any) => o.name}
              value={selectedCity}
              onChange={(e, v) => this.setState({ selectedCity: v })}
              renderInput={(params) => (
                <TextField {...params} label="City *" margin="normal" />
              )}
            />

            <TextField
              fullWidth
              label="Buyer Name *"
              name="buyerName"
              value={formData.buyerName}
              onChange={this.handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Contact Person *"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={this.handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Phone *"
              name="phone"
              value={formData.phone}
              onChange={this.handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="GST No"
              name="gstNo"
              value={formData.gstNo}
              onChange={this.handleChange}
              margin="normal"
            />
            <TextField
              select
              fullWidth
              label="Type *"
              name="type"
              value={formData.type}
              onChange={this.handleChange}
              margin="normal"
            >
              <MenuItem value="" disabled>
                Select Type
              </MenuItem>
              <MenuItem value="Aggregators">Aggregators</MenuItem>
              <MenuItem value="Recyclers">Recyclers</MenuItem>
              <MenuItem value="Upcyclers">Upcyclers</MenuItem>
              <MenuItem value="Co-processors">Co-processors</MenuItem>
              <MenuItem value="Others">Others</MenuItem>
            </TextField>
            {/* UPLOAD MULTIPLE DOCUMENTS */}
            <Button component="label" variant="outlined" sx={{ mt: 2 }}>
              Upload Documents (Multiple)
              <input
                hidden
                type="file"
                multiple
                accept="application/pdf,image/*"
                onChange={(e) => {
                  this.handleMultipleFileUpload(e);
                  e.target.value = "";
                }}
              />
            </Button>
            {documentPreviews.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  {documentPreviews.map((preview, index) => {
                    const isExisting = index < existingDocuments.length;
                    const existingDoc = isExisting
                      ? existingDocuments[index]
                      : null;

                    return (
                      <Chip
                        key={index}
                        label={`Document ${index + 1}`}
                        onDelete={() => {
                          if (isExisting && existingDoc) {
                            this.handleRemoveExistingDocument(existingDoc.id);
                          } else {
                            this.handleRemoveDocument(index);
                          }
                        }}
                        deleteIcon={<CloseIcon />}
                        sx={{ fontWeight: 500 }}
                      />
                    );
                  })}
                </Box>
                <Box
                  sx={{
                    maxHeight: 400,
                    overflowY: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {documentPreviews.map((preview, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>
                        Document {index + 1}
                      </Box>
                      <iframe
                        src={preview}
                        style={{
                          width: "100%",
                          height: 300,
                          border: "1px solid #ddd",
                        }}
                        title={`Preview ${index + 1}`}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              className="customCloseButton"
              onClick={() => this.setState({ showModal: false })}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={this.handleSaveBuyer}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={!!deleteId}
          onClose={() => this.setState({ deleteId: null })}
          maxWidth="xs"
        >
          <DialogTitle>Delete Buyer</DialogTitle>

          <DialogContent>
            Are you sure you want to delete this buyer?
          </DialogContent>

          <DialogActions>
            <Button
              className="customCloseButton"
              onClick={() => this.setState({ deleteId: null })}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={this.handleDeleteBuyer}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default withRouter(withTranslation()(Buyers));
